import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import CheckoutModal from "@/components/CheckoutModal";
import { api, CATEGORIES } from "@/lib/config";
import { useTiers } from "@/lib/useTiers";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, CheckCircle2, ArrowLeft, Sparkles, FileText, Link as LinkIcon, ExternalLink } from "lucide-react";

export default function LessonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tiers } = useTiers();
  const [lesson, setLesson] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [l, c, pr] = await Promise.all([
          api.get(`/lessons/${id}`),
          api.get(`/comments/${id}`),
          api.get("/progress"),
        ]);
        setLesson(l.data);
        setComments(c.data);
        setCompleted(pr.data.completed_ids?.includes(id));
      } catch {
        toast.error("Lesson not found");
        navigate("/learn");
      }
    })();
  }, [id, navigate]);

  const handleComplete = async () => {
    try {
      await api.post(`/lessons/${id}/complete`);
      setCompleted(true);
      toast.success("Marked complete.");
    } catch {
      toast.error("Could not mark complete");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post("/comments", { lesson_id: id, text: newComment });
      setComments([data, ...comments]);
      setNewComment("");
    } catch {
      toast.error("Could not post comment");
    }
  };

  if (!lesson) {
    return <AppShell><div className="text-white/50">Loading…</div></AppShell>;
  }

  const categoryColor = CATEGORIES.find((c) => c.key === lesson.category)?.accent || "#FF8A00";
  const requiredTier = tiers.find((t) => (t.tier_key || t.key) === lesson.tier_required
                                      || t.grants_tier === lesson.tier_required);

  return (
    <AppShell>
      <div className="space-y-8">
        <Link to="/learn" data-testid="back-to-hub" className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white">
          <ArrowLeft size={14} /> Learning Hub
        </Link>

        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: categoryColor }}>
            {lesson.category}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">{lesson.title}</h1>
          <p className="text-white/55 mt-3 max-w-2xl">{lesson.description}</p>
        </div>

        {/* Video */}
        <div className="rounded-2xl overflow-hidden bg-[#0A0A0A] border border-white/5">
          {!lesson.unlocked ? (
            <div className="aspect-video flex flex-col items-center justify-center p-6 text-center"
                 style={{ background: `radial-gradient(circle at 50% 40%, ${categoryColor}30, transparent 70%), #0A0A0A` }}>
              <div className="w-16 h-16 rounded-full bg-black/60 border border-white/10 flex items-center justify-center mb-4">
                <Lock size={22} />
              </div>
              <div className="font-display text-2xl">Locked</div>
              <p className="text-sm text-white/55 mt-2 max-w-sm">
                This lesson is included with the {requiredTier?.name || lesson.tier_required} tier.
              </p>
              <Button data-testid="unlock-lesson-btn"
                onClick={() => setUpgradeOpen(true)}
                className="mt-5 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                Unlock for {requiredTier?.price || ""}
              </Button>
            </div>
          ) : (
            <LessonPlayer lesson={lesson} />
          )}
        </div>

        {lesson.unlocked && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Takeaways + actions */}
            <div className="lg:col-span-8 space-y-6">
              <div className="glass rounded-2xl p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00]">Key takeaways</div>
                <ul className="mt-4 space-y-3">
                  {lesson.key_takeaways?.map((k, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px]">
                      <Sparkles size={16} className="text-[#FF8A00] mt-1 shrink-0" /> {k}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass rounded-2xl p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#E52E71]">Action steps</div>
                <ul className="mt-4 space-y-3">
                  {lesson.action_steps?.map((k, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px]">
                      <div className="w-6 h-6 rounded-full border border-[#E52E71]/40 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[11px] text-[#E52E71]">{i+1}</span>
                      </div>
                      {k}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Comments */}
              <div className="glass rounded-2xl p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mb-4">Discussion ({comments.length})</div>
                <form onSubmit={submitComment} className="mb-5 flex gap-3">
                  <input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your take…"
                    data-testid="comment-input"
                    className="flex-1 bg-[#0F0F0F] border border-white/5 rounded-lg px-4 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#FF8A00]/40"
                  />
                  <Button type="submit" data-testid="comment-submit"
                    className="rounded-lg bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">Post</Button>
                </form>
                <div className="space-y-4">
                  {comments.map((c) => (
                    <div key={c.comment_id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-xs font-semibold shrink-0">
                        {c.user_name?.[0]?.toUpperCase() || "M"}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm"><span className="font-medium">{c.user_name}</span>
                          <span className="text-white/30 text-[11px] ml-2">{formatTime(c.created_at)}</span>
                        </div>
                        <p className="text-sm text-white/75 mt-1">{c.text}</p>
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && <p className="text-sm text-white/35">Be the first to share your thoughts.</p>}
                </div>
              </div>
            </div>

            {/* Right rail */}
            <div className="lg:col-span-4 space-y-4">
              <div className="glass rounded-2xl p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/50">Mark complete</div>
                <p className="text-sm text-white/60 mt-2 mb-4">Track your progress across the library.</p>
                <Button
                  onClick={handleComplete}
                  disabled={completed}
                  data-testid="mark-complete-btn"
                  className={`w-full rounded-full ${completed ? "bg-white/10 text-white/60" : "bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"}`}
                >
                  {completed ? (<><CheckCircle2 className="mr-2" size={14}/> Completed</>) : "Mark complete"}
                </Button>
              </div>
              <div className="glass rounded-2xl p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mb-3">
                  Resources {lesson.resources?.length ? `(${lesson.resources.length})` : ""}
                </div>
                {lesson.resources?.length ? (
                  <div className="space-y-2">
                    {lesson.resources.map((r) => (
                      <ResourceItem key={r.resource_id} resource={r} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-white/40">No downloads for this lesson yet.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <CheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} tierKey={lesson.tier_required} />
    </AppShell>
  );
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

function ResourceItem({ resource }) {
  const [opening, setOpening] = useState(false);
  const isLink = resource.type === "link";

  const handleClick = async (e) => {
    if (isLink) return; // native anchor will handle
    e.preventDefault();
    setOpening(true);
    try {
      const { data } = await api.get("/media/token");
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const url = `${BACKEND}/api/media/pdf/${resource.file_id}?auth=${encodeURIComponent(data.token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not open PDF");
    } finally {
      setOpening(false);
    }
  };

  const Inner = (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-white/5 hover:border-[#FF8A00]/30 transition-colors group">
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF8A00]/20 to-[#E52E71]/10 flex items-center justify-center shrink-0">
        {isLink ? <LinkIcon size={16} className="text-[#FF8A00]" /> : <FileText size={16} className="text-[#FF8A00]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{resource.title}</div>
        <div className="text-[11px] text-white/40 truncate">
          {isLink ? new URL(resource.url).hostname : `PDF${resource.size ? ` · ${(resource.size/1024).toFixed(0)} KB` : ""}`}
        </div>
      </div>
      <ExternalLink size={13} className="text-white/30 group-hover:text-[#FF8A00] shrink-0" />
    </div>
  );

  if (isLink) {
    return (
      <a href={resource.url} target="_blank" rel="noreferrer"
         data-testid={`lesson-resource-${resource.resource_id}`}
         className="block">
        {Inner}
      </a>
    );
  }
  return (
    <button onClick={handleClick} disabled={opening}
            data-testid={`lesson-resource-${resource.resource_id}`}
            className="block w-full text-left">
      {Inner}
    </button>
  );
}

function LessonPlayer({ lesson }) {
  const [mediaToken, setMediaToken] = useState(null);
  const hasVimeo = !!lesson.vimeo_video_id;
  const hasFile = !hasVimeo && !!lesson.video_file_id;
  useEffect(() => {
    if (!hasFile) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/media/token");
        if (alive) setMediaToken(data.token);
      } catch {}
    })();
    return () => { alive = false; };
  }, [hasFile]);

  // Vimeo takes priority — private embed with domain restriction.
  if (hasVimeo) {
    const src = `https://player.vimeo.com/video/${lesson.vimeo_video_id}?dnt=1&pip=0`;
    return (
      <div className="aspect-video-wrap">
        <iframe
          src={src}
          title={lesson.title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          data-testid="lesson-video"
          style={{ border: 0 }}
        />
      </div>
    );
  }

  if (hasFile) {
    if (!mediaToken) {
      return <div className="aspect-video flex items-center justify-center text-white/50 text-sm">Preparing secure stream…</div>;
    }
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const src = `${BACKEND}/api/media/video/${lesson.video_file_id}?auth=${encodeURIComponent(mediaToken)}`;
    return (
      <video
        src={src}
        controls
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        playsInline
        onContextMenu={(e) => e.preventDefault()}
        className="w-full aspect-video bg-black"
        data-testid="lesson-video"
      />
    );
  }
  return (
    <div className="aspect-video-wrap">
      <iframe
        src={lesson.video_url}
        title={lesson.title}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="lesson-video"
        style={{ border: 0 }}
      />
    </div>
  );
}
